import LANG_CONST from 'src/helpers/lang/const';
import moment from 'moment';
import { dateOfWeek, remCss, rgba, lang, number, scssVariables as variables } from 'src/helpers';
import React, { useEffect } from 'react';
import { MCButton, MCText, ToggleSwitch } from 'src/components/basic';
import { View } from '../basic';

export const generateSlots = (startTime, endTime, duration) => {
  const start = startTime.clone();
  const slots = [];

  while (start.isBefore(endTime)) {
    slots.push({
      start: start.valueOf(),
      end: start.add(duration, 'minute').valueOf(),
      enabled: true
    });
  }

  return slots;
};

export const generateDurationIntervals = () => {
  const intervals = [];
  // min is 20 minutes
  for (let i = 20; i <= 60; i += 5) {
    intervals.push({ value: i, label: `${i} minutes` });
  }
  return intervals;
};

export const validSlot = (slot, slots = []) => {
  const invalid = { length: 0 };
  if (!slot.startDate || !slot.endDate) {
    invalid.time = 'Time is required';
    invalid.length++;
  } else if (slot.startDate >= slot.endDate) {
    invalid.time = LANG_CONST.validation.start_end_time;
    invalid.length++;
  } else if (
    slots &&
    slots.find((time) => {
      return (
        time.id !== slot.id &&
        !!time.startDate &&
        !!time.endDate &&
        moment(time.endDate).isAfter(slot.startDate, 'm') &&
        moment(time.startDate).isBefore(slot.endDate, 'm') &&
        !time.deleted
      );
    })
  ) {
    invalid.time = LANG_CONST.validation.conflict_slot_time;
    invalid.length++;
  } else if ((slot.endDate - slot.startDate) / 60000 < 20) {
    invalid.time = LANG_CONST.validation.time_slot_min;
    invalid.length++;
  }
  if (slot.appointmentTypes.includes('in_office') && !slot.hospitalId) {
    invalid.location = 'Location is required';
    invalid.length++;
  }

  return invalid;
};

export const validDays = (days, hospital) => {
  const invalid = { length: 0 };
  const hasConflict = (times, start, end, index) =>
    times.find(
      (time, i) =>
        i !== index &&
        !!time.startTime &&
        !!time.endTime &&
        time.endTime > start &&
        time.startTime < end
    );
  days.forEach((day) => {
    if (day.enabled) {
      day.times.forEach((time, timeIndex) => {
        if (!time.startTime || !time.endTime) {
          invalid[day.id + '_' + timeIndex + '_time'] = 'Time is required';
          invalid.length++;
        } else if (time.startTime >= time.endTime) {
          invalid[day.id + '_' + timeIndex + '_time'] = LANG_CONST.validation.start_end_time;
          invalid.length++;
        } else if (hasConflict(day.times, time.startTime, time.endTime, timeIndex)) {
          invalid[day.id + '_' + timeIndex + '_time'] = LANG_CONST.validation.conflict_slot_time;
          invalid.length++;
        } else if ((time.endTime - time.startTime) / 60000 < 20) {
          invalid[day.id + '_' + timeIndex + '_time'] = LANG_CONST.validation.time_slot_min;
          invalid.length++;
        }
        if (time.types.includes('in_office') && !time.hospitalId && !hospital) {
          invalid[day.id + '_' + timeIndex + '_location'] = 'Location is required';
          invalid.length++;
        }
      });
    }
  });

  return invalid;
};

export const validWeeks = (weeks, hospital) => {
  const mainInvalid = { length: 0, weeks: [] };

  weeks.forEach(({ days, enabled }) => {
    let invalid = { length: 0 };
    if (enabled) {
      invalid = validDays(days, hospital);
    }
    mainInvalid.weeks.push(invalid);
    mainInvalid.length += invalid.length;
  });

  return mainInvalid;
};

export const generateWorkingWeeks = (days, startDate, endDate) => {
  const weeks = {};
  const numberOfWeek = 4;

  const endYear = endDate
    ? moment(endDate)
    : moment()
        .add(numberOfWeek - 1, 'week')
        .endOf('isoWeek');

  for (
    let dayOfWeek = moment(startDate).startOf('isoWeek');
    !dayOfWeek.isAfter(endYear, 'day');
    dayOfWeek.add(7, 'days')
  ) {
    let startOfWeek = dayOfWeek.clone().startOf('isoWeek');

    if (startOfWeek.isBefore(moment(), 'day')) {
      startOfWeek = moment();
    }
    if (startOfWeek.isBefore(startDate, 'day')) {
      startOfWeek = moment(startDate);
    }

    let endOfWeek = dayOfWeek.clone().endOf('isoWeek');

    if (endOfWeek.isAfter(endYear, 'day')) {
      endOfWeek = endYear.clone();
    }

    const weekId = startOfWeek.format('YYYYMMDD') + '_' + endOfWeek.format('YYYYMMDD');

    if (!weeks[weekId]) {
      const generateDays = [];

      for (let day = startOfWeek.clone(); !day.isAfter(endOfWeek, 'day'); day.add(1, 'day')) {
        const find = days.find((d) => d.id === day.format('dddd').toLowerCase());
        generateDays.push({ ...find, label: day.format('dddd (MMMM DD)'), value: day.valueOf() });
      }
      weeks[weekId] = {
        id: weekId,
        startOfWeek: startOfWeek.valueOf(),
        endOfWeek: endOfWeek.valueOf(),
        enabled: true,
        label: startOfWeek.format('MMMM DD') + ' to ' + endOfWeek.format('MMMM DD'),
        days: generateDays
      };
    }
  }

  return Object.values(weeks);
};

export const generateWorkingDays = (hospital, duration) => {
  const types = ['virtual'];
  if (hospital) {
    types.push('in_office');
  }

  return dateOfWeek.map((k) => {
    const startTime = moment().hour(9).startOf('hour');
    const endTime = startTime.clone().add(8, 'h').startOf('hour');

    return {
      id: k.toLowerCase(),
      label: k,
      enabled: true,
      times: [
        {
          ...hospital,
          startTime: startTime.valueOf(),
          endTime: endTime.valueOf(),
          types,
          slots: generateSlots(startTime, endTime, duration)
        }
      ]
    };
  });
};

export const generateData = (weeks) => {
  const days = [];
  weeks.forEach((week) => {
    if (week?.enabled) {
      week.days?.forEach((day) => {
        if (day?.enabled) {
          day.times.forEach((time) => {
            days.push({
              day: day.value,
              types: time.types,
              slots: time.slots
                .filter((s) => s.enabled)
                .map((slot) => moment(slot.start).format('HH:mm'))
            });
          });
        }
      });
    }
  });
  return days;
};

export const generateUnsavedSlots = (weeks, hospital) => {
  const slots = [];
  weeks.forEach((week) => {
    if (week?.enabled) {
      week.days?.forEach((day) => {
        if (day?.enabled) {
          day.times.forEach((time) => {
            time.slots.forEach((slot) => {
              if (slot.enabled) {
                const startMoment = moment(day.value);
                const startTimeObj = moment(slot.start);
                const endTimeObj = moment(slot.end);
                const startDate = startMoment
                  .clone()
                  .hours(startTimeObj.hours())
                  .minutes(startTimeObj.minutes())
                  .valueOf();
                const endDate = startMoment
                  .clone()
                  .hours(endTimeObj.hours())
                  .minutes(endTimeObj.minutes())
                  .valueOf();
                slots.push({
                  id: startDate + '_' + endDate,
                  startDate,
                  endDate,
                  unsaved: false,
                  appointmentTypes: time.types,
                  ...hospital
                });
              }
            });
          });
        }
      });
    }
  });
  return slots;
};

export const updateGroup = (list, group, id, enabled) => {
  const updateItemAndChildren = (items, group, id, enabled) => {
    for (const item of items) {
      if (group === item.group && item.id === id) {
        item.enabled = enabled;
        if (item.items)
          item.items.forEach((i) => updateItemAndChildren(item.items, i.group, i.id, enabled));
        return list;
      }
      if (item.items) updateItemAndChildren(item.items, group, id, enabled);
    }
    return list;
  };
  const updateALl = (items) => {
    for (const item of items) {
      if (item.items) {
        item.items = updateALl(item.items);
        item.enabled = !!item.items.find((i) => i.enabled !== false);
      }
    }
    return items;
  };

  if (group) updateItemAndChildren(list, group, id, enabled);

  return updateALl(list);
};

export const rangeTimeOfGroup = (item, parent) => {
  if (item.group === 'month') {
    return {
      startDate: Math.max(moment(item.id).startOf('month').valueOf(), moment().valueOf()),
      endDate: moment(item.id).endOf('month').valueOf()
    };
  } else if (item.group === 'week') {
    const endDate = Math.min(
      moment(item.id).endOf('isoWeek').valueOf(),
      moment(parent.id).endOf('month').valueOf()
    );
    return { startDate: Math.max(Date.now(), item.id, parent.id), endDate };
  } else {
    return {
      startDate: Math.max(moment(item.id).startOf('day').valueOf(), moment().valueOf()),
      endDate: moment(item.id).endOf('day').valueOf()
    };
  }
};

export const getFreeTimeForNewSlot = (slots, day, startHour = 9, limitHour) => {
  const minDuration = 20;
  let currentPointer = moment(day).startOf('day').hour(startHour);
  if (!currentPointer?.isAfter(moment())) {
    currentPointer = moment().startOf('hour').add(1, 'hour');
  }
  const limit = limitHour
    ? moment(day).endOf('day').hour(limitHour)
    : moment(day).endOf('day').minute(55);
  const sorted = slots.filter((s) => !s.deleted);

  for (const slot of sorted) {
    const start = moment(slot.startDate);
    const end = moment(slot.endDate);
    // Calculate difference in minutes
    const diff = start.diff(currentPointer, 'minutes');

    if (diff >= minDuration) {
      // Found the first gap!
      return {
        startDate: currentPointer.clone().valueOf(),
        endDate: currentPointer.clone().add(minDuration, 'm').valueOf()
      };
    }

    // Move pointer to the end of this slot if it's further than current
    if (end.isAfter(currentPointer)) {
      currentPointer = end.clone();
    }
  }

  // Check the gap between the last slot and the end of the day
  if (limit.diff(currentPointer, 'minutes') >= minDuration) {
    return {
      startDate: currentPointer.clone().valueOf(),
      endDate: currentPointer.clone().add(minDuration, 'm').valueOf()
    };
  }

  return startHour > 1 ? getFreeTimeForNewSlot(slots, day, startHour - 1, startHour) : null;
};

export const Toggle = ({ slot, toggleSlot, label, reverse, disabled, isAsync = false }) => {
  const [enabled, setEnabled] = React.useState(slot.enabled !== false);
  const loading = React.useRef(false);

  const onClick = (e) => {
    e.preventDefault();
    if (isAsync)
      return setEnabled((o) => {
        toggleSlot(slot, !o);
        return o;
      });
    if (loading.current) return;
    loading.current = true;
    setEnabled((o) => {
      setTimeout(() => {
        toggleSlot(slot, !o, (err) => {
          if (err) setEnabled(o);
          loading.current = false;
        });
      }, 0);
      return !o;
    });
  };

  useEffect(() => {
    if (loading.current) return;
    setEnabled(slot.enabled !== false);
  }, [slot.enabled]);

  if (reverse)
    return (
      <>
        {label || (
          <div onClick={onClick} className={'mc-value-520 mc-pointer'}>
            {moment(slot.startDate).format('HH:mm [to] ') + moment(slot.endDate).format('HH:mm')}
          </div>
        )}
        <ToggleSwitch
          checked={enabled}
          onChange={onClick}
          className={disabled ? 'disabled' : ''}
          disabled={disabled}
        />
      </>
    );
  return (
    <>
      <ToggleSwitch
        checked={enabled}
        onChange={onClick}
        className={disabled ? 'disabled' : ''}
        disabled={disabled}
      />
      {label || (
        <div onClick={onClick} className={'mc-value-520 mc-pointer'}>
          {moment(slot.startDate).format('HH:mm [to] ') + moment(slot.endDate).format('HH:mm')}
        </div>
      )}
    </>
  );
};

export const SlotTag = ({ item, parent, onSlotTag }) => {
  return (
    <View
      style={{
        flexDirection: 'row',
        flex: 1,
        gap: remCss(15),
        flexWrap: 'wrap',
        justifyContent: 'center'
      }}
    >
      {item.hasSlot ? (
        <>
          <MCButton
            primary
            light
            rounded
            small
            hoverOpacity
            style={{
              gap: remCss(10),
              width: remCss(150),
              backgroundColor: rgba(variables['colors-mc-primary'], 0.2)
            }}
            onClick={onSlotTag ? () => onSlotTag(item, parent, 'free') : null}
          >
            <MCText
              label={lang.t(
                item.free > 1 ? LANG_CONST.text.free_slots : LANG_CONST.text.free_slot,
                { num: number.format(item.free) }
              )}
              heading
              size={16}
              lineHeight={16}
              numberOfLines={1}
            />
          </MCButton>
          <MCButton
            quaternary
            light
            rounded
            small
            hoverOpacity
            onClick={onSlotTag ? () => onSlotTag(item, parent, 'booked') : null}
            style={{
              gap: remCss(10),
              width: remCss(150),
              backgroundColor: rgba(variables['colors-mc-quaternary'], 0.17)
            }}
          >
            <MCText
              label={lang.t(
                item.booked > 1 ? LANG_CONST.text.appointments : LANG_CONST.text.appointment,
                { num: number.format(item.booked) }
              )}
              heading
              size={16}
              lineHeight={16}
              numberOfLines={1}
            />
          </MCButton>
        </>
      ) : (
        <MCButton
          blacklight
          outline
          rounded
          small
          transparent
          hoverOpacity
          onClick={onSlotTag ? () => onSlotTag(item, parent, 'free') : null}
          style={{
            gap: remCss(10),
            width: remCss(200),
            borderColor: rgba(variables['colors-mc-heading'], 0.15)
          }}
        >
          <MCText
            label={LANG_CONST.text.no_schedule}
            heading
            size={16}
            lineHeight={16}
            numberOfLines={1}
          />
        </MCButton>
      )}
    </View>
  );
};
